import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Phone, CreditCard, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { registerUser, adminUser, memberUser, logout, users } = useApp();
    const currentUser = adminUser || memberUser;

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [formData, setFormData] = useState({
        sponsorId: '',
        name: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: ''
    });

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const sponsor = queryParams.get('sponsor');
        if (sponsor) {
            setFormData(prev => ({ ...prev, sponsorId: sponsor }));
        }
    }, [location]);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        setError('');
        setLoading(true);
        console.log("🚀 Starting public Registration...");

        try {
            // Passwords match check
            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match');
                setLoading(false);
                return;
            }

            // Phone Validation
            const cleanMobile = (formData.mobile || '').trim();
            if (!/^\d{10}$/.test(cleanMobile)) {
                setError('Please enter a valid 10-digit phone number');
                setLoading(false);
                return;
            }

            const cleanEmail = (formData.email || '').trim().toLowerCase();
            const cleanSponsorId = (formData.sponsorId || '').trim().toUpperCase();
            const cleanName = (formData.name || '').trim();

            console.log("📦 Data ready for registerUser:", { cleanName, cleanEmail, cleanMobile, cleanSponsorId });

            const result = await registerUser({
                ...formData,
                name: cleanName,
                email: cleanEmail,
                mobile: cleanMobile,
                sponsorId: cleanSponsorId
            });

            console.log("🏁 Registration result:", result);

            if (result.success) {
                alert(`SUCCESS! New Member Registered.\nCustomer ID: ${result.user.id}`);

                if (currentUser) {
                    // Start fresh or Go back
                    if (window.confirm("Do you want to register another member?")) {
                        setFormData(prev => ({
                            ...prev,
                            name: '',
                            email: '',
                            mobile: '',
                            password: '',
                            confirmPassword: ''
                            // Keep sponsorId as is
                        }));
                        setLoading(false);
                    } else {
                        navigate(-1); // Go back to dashboard/tree
                    }
                } else {
                    navigate('/login');
                }
            } else {
                setError(result.message || 'Registration failed');
                alert("REGISTRATION FAILED: " + result.message);
                setLoading(false);
            }
        } catch (error) {
            console.error("❌ Registration Handler Error:", error);
            setError("Critical Error: " + error.message);
            alert("CRITICAL ERROR: " + error.message);
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex flex-col justify-center sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="text-center">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-600/30">
                        <span className="text-white font-bold text-xl">P</span>
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 mb-2">Customer Registration</h2>
                    <p className="text-gray-500 mb-8 font-medium">Join our growing network today</p>
                </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 border border-gray-100">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {error && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm font-medium border border-red-100">
                                {error}
                            </div>
                        )}
                        {/* Sponsor ID */}

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Sponsor ID (Mandatory)</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="sponsorId"
                                    value={formData.sponsorId}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 transition-colors"
                                    placeholder="Referral Code"
                                />
                            </div>
                            {formData.sponsorId && users && (() => {
                                const sponsor = users.find(u => String(u.id).toUpperCase() === String(formData.sponsorId).toUpperCase());
                                return sponsor ? (
                                    <p className="mt-1 text-xs font-bold text-emerald-600 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" /> Sponsor: {sponsor.name}
                                    </p>
                                ) : (
                                    <p className="mt-1 text-xs font-bold text-rose-500">
                                        Invalid Sponsor ID
                                    </p>
                                );
                            })()}
                        </div>

                        {/* Full Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Full Name</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="John Doe"
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email Address</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="tel"
                                    name="mobile"
                                    required
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="9876543210"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Password</label>
                            <div className="mt-1 flex items-center bg-white border border-gray-300 rounded-2xl focus-within:ring-4 focus-within:ring-primary-100 focus-within:border-primary-500 shadow-sm transition-all overflow-hidden relative" style={{ border: '1px solid #d1d5db', borderRadius: '1rem', display: 'flex', alignItems: 'center', backgroundColor: '#fff', position: 'relative' }}>
                                <div className="pl-3 flex items-center pointer-events-none text-gray-400" style={{ paddingLeft: '0.75rem', display: 'flex', alignItems: 'center', color: '#9ca3af' }}>
                                    <Lock className="h-5 w-5" />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="block w-full pl-3 pr-3 py-3.5 border-none focus:ring-0 text-sm font-semibold text-gray-900 placeholder-gray-400 bg-transparent"
                                    style={{ border: 'none', outline: 'none', flex: '1', padding: '0.875rem 0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#111827', backgroundColor: 'transparent' }}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    tabIndex="-1"
                                    className="px-4 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer outline-none bg-transparent"
                                    style={{ padding: '0 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none', height: '100%' }}
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? "Hide Password" : "Show Password"}
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563' }}>
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                            <line x1="2" y1="2" x2="22" y2="22"></line>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563' }}>
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                            <div className="mt-1 flex items-center bg-white border border-gray-300 rounded-2xl focus-within:ring-4 focus-within:ring-primary-100 focus-within:border-primary-500 shadow-sm transition-all overflow-hidden relative" style={{ border: '1px solid #d1d5db', borderRadius: '1rem', display: 'flex', alignItems: 'center', backgroundColor: '#fff', position: 'relative' }}>
                                <div className="pl-3 flex items-center pointer-events-none text-gray-400" style={{ paddingLeft: '0.75rem', display: 'flex', alignItems: 'center', color: '#9ca3af' }}>
                                    <Lock className="h-5 w-5" />
                                </div>
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="block w-full pl-3 pr-3 py-3.5 border-none focus:ring-0 text-sm font-semibold text-gray-900 placeholder-gray-400 bg-transparent"
                                    style={{ border: 'none', outline: 'none', flex: '1', padding: '0.875rem 0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#111827', backgroundColor: 'transparent' }}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    tabIndex="-1"
                                    className="px-4 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer outline-none bg-transparent"
                                    style={{ padding: '0 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none', height: '100%' }}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    title={showConfirmPassword ? "Hide Password" : "Show Password"}
                                >
                                    {showConfirmPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563' }}>
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                            <line x1="2" y1="2" x2="22" y2="22"></line>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563' }}>
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-md text-sm font-medium text-white transition-all hover:shadow-lg ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'}`}
                            >
                                {loading ? 'Processing...' : (
                                    <>Create Account <ArrowRight className="ml-2 w-4 h-4" /></>
                                )}
                            </button>
                        </div>
                    </form>

                </div>
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                        Already a member?{' '}
                        <Link to="/login" className="font-bold text-primary-600 hover:text-primary-500 transition-colors">
                            Login
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;
